import { ReactNode } from 'react';
import { ButtonSize, ButtonType } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { UnstyledLinkProps } from '@ardoq/link';
import type { PopoverTrigger } from '@ardoq/popovers';
import type { StatusType } from '@ardoq/status-ui';

export type ToggleSidebarButtonProps = {
  onClick: VoidFunction;
  onHover: VoidFunction;
};

export type BreadcrumbItem = {
  label: string;
  htmlId?: string;
} & UnstyledLinkProps;

type ButtonState = 'idle' | 'loading' | 'disabled' | 'active';

type CommonActionMenuIconProps = {
  name: IconName;
  position?: 'left' | 'right';
};

export type CommonActionMenuItemProps = {
  label: string;
  state?: ButtonState;
  buttonType?: ButtonType;
  dataTourId?: string;
  icon?: CommonActionMenuIconProps;
};

export type ActionMenuStatusDot = {
  statusType: StatusType;
  /** Announced as part of the button's accessible name, since the dot is color only. */
  label: string;
};

export type ActionMenuDivider = {
  type: 'divider';
  size: 'default' | 'section';
};

export type ActionMenuButton = {
  type: 'button';
  buttonType: ButtonType;
  buttonSize?: ButtonSize;
  onClick: VoidFunction;
  tooltipText?: string;
  icon?: CommonActionMenuIconProps & {
    statusDot?: ActionMenuStatusDot;
  };
  /** Makes the button the trigger of the `PopoverAtAnchor` with this id. */
  popoverTrigger?: PopoverTrigger;
  /** DOM id an external script (e.g. Beamer) can target to anchor a badge. */
  badgeAnchorId?: string;
} & CommonActionMenuItemProps;

export type ActionMenuIconButton = {
  type: 'icon-button';
  buttonType: ButtonType;
  icon: {
    name: IconName;
  };
  onClick: VoidFunction;
} & CommonActionMenuItemProps;

export type ActionMenuDropdownButton = {
  type: 'dropdown';
  buttonType: ButtonType;
  options: DropdownItem[];
  isIconOnly?: boolean;
  maxDropdownSize?: DropdownSize;
  onMenuOpen?: VoidFunction;
} & CommonActionMenuItemProps;

export type ActionMenuLinkButton = {
  type: 'link';
  buttonType: ButtonType;
  isIconOnly: boolean;
  icon: {
    name: IconName;
  };
  label: string;
} & UnstyledLinkProps;

export type ActionMenuAiButton = {
  type: 'ai-button';
  onClick: VoidFunction;
  hasNotification?: boolean;
} & CommonActionMenuItemProps;

export type ActionMenuItem =
  | ActionMenuDivider
  | ActionMenuButton
  | ActionMenuIconButton
  | ActionMenuDropdownButton
  | ActionMenuLinkButton
  | ActionMenuAiButton;

export const isIconButton = (
  action: ActionMenuItem
): action is ActionMenuIconButton => {
  return action.type === 'icon-button';
};

export const isDropdownButton = (
  action: ActionMenuItem
): action is ActionMenuDropdownButton => {
  return action.type === 'dropdown';
};

export const isLinkButton = (
  action: ActionMenuItem
): action is ActionMenuLinkButton => {
  return action.type === 'link';
};

export const isAiButton = (
  action: ActionMenuItem
): action is ActionMenuAiButton => {
  return action.type === 'ai-button';
};

type HelpItemBase = {
  label: string;
  rightContent?: ReactNode;
  description?: string;
  icon?: { name: IconName };
  onTrack?: VoidFunction;
  section: 'tile' | 'whats-new' | 'tour' | 'article';
};

export type HelpHeaderItem = {
  type: 'header';
  label: string;
  description?: string;
};

/**
 * Overrides the Help side panel's "On this page" title/description. Ignored
 * by the anchored Help dropdown, unlike `HelpHeaderItem`.
 */
export type HelpPanelHeaderItem = {
  type: 'panel-header';
  label: string;
  description?: string;
};

export type HelpLinkItem = HelpItemBase &
  UnstyledLinkProps & {
    type: 'link';
  };

export type HelpActionItem = HelpItemBase & {
  type: 'action';
  onClick: VoidFunction;
};

/**
 * Embedded video, shown in the Help side panel's "Videos" section. Ignored
 * by the anchored Help dropdown.
 */
export type HelpVideoItem = {
  type: 'video';
  label: string;
  src: string;
};

export type HelpItem =
  | HelpHeaderItem
  | HelpPanelHeaderItem
  | HelpLinkItem
  | HelpActionItem
  | HelpVideoItem;

export type HelpActionableItem = Exclude<
  HelpItem,
  { type: 'header' } | { type: 'panel-header' } | { type: 'video' }
>;
